<template>
  <div class="page">
    <div class="container">
      <h1>Welcome to LMP</h1>
      <p>DOF Legislative Management Platform - Your gateway to legislative information</p>
      
      <!-- Dashboard Section -->
      <Panel header="Legislative Dashboard" class="mb-4">
        <!-- Current Session -->
        <div class="countdown-container mt-6">
          <h3>Current Session: {{ currentSession.name }}</h3>
          <div class="session-dates" v-if="currentSession.dateFrom">
            <div class="date-range">
              <span class="date-item">{{ formatDate(currentSession.dateFrom) }}</span>
              <span class="date-separator">—</span>
              <span class="date-item">{{ formatDate(currentSession.dateTo) }}</span>
            </div>
          </div>
        </div>
        
        <!-- Next Session Countdown -->
        <div class="next-session-container mt-4" v-if="nextSession.name">
          <h4>Next Session: {{ nextSession.name }}</h4>
          <div class="countdown-display">
            <Tag :value="countdown.days + ' Days'" severity="danger" class="mr-2" />
            <Tag :value="countdown.hours + ' Hours'" severity="warn" class="mr-2" />
            <Tag :value="countdown.minutes + ' Minutes'" severity="info" />
          </div>
        </div>
        
        <!-- Four Tiles -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <Card :class="{ 'tile-loading': loadingRequestForComments }" @click="showRequestForCommentsModal">
            <template #title>Request for Comments</template>
            <template #content>
              <div v-if="loadingRequestForComments" class="loading-content">
                <ProgressSpinner style="width: 30px; height: 30px" strokeWidth="4" />
                <div>Loading...</div>
              </div>
              <div v-else>
                <div class="tile-count">{{ requestForComments }}</div>
              
                <Button label="View All" outlined />
              </div>
            </template>
          </Card>
          <Card @click="showSignedPositionModal" :class="{ 'tile-loading': loadingSignedPosition }">
            <template #title>Signed Position Papers</template>
            <template #content>
              <div v-if="loadingSignedPosition" class="loading-content">
                <ProgressSpinner style="width: 30px; height: 30px" strokeWidth="4" />
                <div>Loading...</div>
              </div>
              <div v-else>
                <div class="tile-count">{{ signedPositionCount }}</div>
                
                <Button label="View All" outlined />
              </div>
            </template>
          </Card>
          <Card :class="{ 'tile-loading': loadingForReview }" @click="showForReviewsModal">
            <template #title>Ongoing Reviews</template>
            <template #content>
              <div v-if="loadingForReview" class="loading-content">
                <ProgressSpinner style="width: 30px; height: 30px" strokeWidth="4" />
                <div>Loading...</div>
              </div>
              <div v-else>
                <div class="tile-count">{{ forReviewCount }}</div>
                <!--<div class="tile-count">-</div>-->
                <Button label="View All" outlined />
              </div>
            </template>
          </Card>
          
          <Card>
            <template #title>Top 10 Active Committees</template>
            <template #content>
              <!--<div class="tile-count">10</div>-->
              <div class="tile-count">-</div>
              <Button label="View List" outlined @click="showCommitteeReport" />
            </template>
          </Card>
          
        </div>
      </Panel>

            <!-- Top 5 Urgent Panel -->
      <Panel class="mb-4">
        <template #header>
          <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
            <span>Top 5 Urgent</span>
            <Button label="Print" icon="pi pi-print" @click="printTop5Urgent" size="small" />
          </div>
        </template>
        <DataTable :value="top5UrgentBills" size="small" showGridlines="true" scrollable scrollHeight="400px">
          <Column field="subject_title" header="Subject Title" style="min-width: 200px" headerStyle="text-align: center" />
          <Column field="urgency" header="Urgency" style="min-width: 100px" headerStyle="text-align: center">
            <template #body="{ data }">
              <Tag :value="data.urgency" :severity="getUrgencySeverity(data.urgency)" />
            </template>
          </Column>
          <Column field="bill_type" header="Bill Type" style="min-width: 120px" headerStyle="text-align: center" />
          <Column field="status" header="Status" style="min-width: 150px" headerStyle="text-align: center" />
          <Column field="total_revenue_impact" header="Total Revenue Impact (2026-2028)" style="min-width: 180px" />
          <Column field="total_budgetary_impact" header="Total Budgetary Impact (2026-2028)" style="min-width: 180px" />
          <Column field="total_fiscal_impact" header="Total Fiscal Impact (2026-2028)" style="min-width: 180px" />
          <Column field="other_dof_concerns" header="Other DOF Concerns" style="min-width: 150px" />
          <Column field="background_rationale" header="Background/ Rationale" style="min-width: 200px" />
          <Column field="current_existing_regime" header="Current/ Existing Regime" style="min-width: 200px" />
          <Column field="proposal_salient_provisions" header="Proposal/ Salient Provisions" style="min-width: 200px" />
          <Column field="fpmg_recommended_position" header="FPMG Recommended Position" style="min-width: 180px" />
          <Column field="osec_instruction_remarks" header="OSEC Instruction / Remarks" style="min-width: 200px" />
          <Column header="Actions" style="min-width: 100px">
            <template #body="{ data }">
              <Button icon="pi pi-pencil" size="small" severity="info" @click="viewSubject(data)" />
            </template>
          </Column>
        </DataTable>
      </Panel>


      <!-- News Advisory Section -->
      <Panel header="New Advisory" class="mb-4">
        <Carousel :value="newsItems" :numVisible="1" :numScroll="1" :autoplayInterval="5000">
          <template #item="slotProps">
            <div class="news-item">
              <h3><a :href="slotProps.data.link" target="_blank" class="news-headline-link">{{ slotProps.data.title }}</a></h3>
              <p>{{ slotProps.data.excerpt }}</p>
              <small class="news-date">{{ slotProps.data.date }}</small>
              <div class="mt-3">
                <Button label="Read More" size="small" @click="openNewsLink(slotProps.data.link)" />
              </div>
            </div>
          </template>
        </Carousel>
      </Panel>

      <!-- Livestream Section -->
      <Panel header="Watch Livestream" class="mb-4">
        <div class="flex gap-4 flex-wrap mt-6">
          <Card class="flex-1 stream-card" style="min-width: 300px;">
            <template #title>Senate Session</template>
            <template #content>
              <div class="stream-content">
                <div class="stream-info">
                  <p>Watch live proceedings from the Senate of the Philippines</p>
                </div>
                <Button label="🔴 Watch Live" severity="danger" @click="openSenateStream" class="stream-btn" />
              </div>
            </template>
          </Card>
          <Card class="flex-1 stream-card" style="min-width: 300px;">
            <template #title>House Session</template>
            <template #content>
              <div class="stream-content">
                <div class="stream-info">
                  <p>Watch live proceedings from the House of Representatives</p>
                </div>
                <Button label="🔴 Watch Live" severity="danger" @click="openHouseStream" class="stream-btn" />
              </div>
            </template>
          </Card>
        </div>
      </Panel>

      <!-- Committee Meetings Tabs -->
      <Panel header="Committee Meetings" class="mb-4">
        <div class="committee-controls">
          <InputText v-model="searchTerm" placeholder="Search committees..." class="search-input" />
          <Button label="Print Weekly Schedule" icon="pi pi-print" class="print-btn" />
        </div>
        <TabView>
          <TabPanel header="Senate">
            <DataTable :value="filteredSenateMeetings" size="small">
              <Column field="committee" header="Committee" />
              <Column field="date" header="Date" />
              <Column field="time" header="Time" />
            </DataTable>
          </TabPanel>
          <TabPanel header="House">
            <DataTable :value="filteredHouseMeetings" size="small">
              <Column field="committee" header="Committee" />
              <Column field="date" header="Date" />
              <Column field="time" header="Time" />
            </DataTable>
          </TabPanel>
          <TabPanel header="Other Invitations">
            <DataTable :value="filteredDofMeetings" size="small">
              <Column field="committee" header="Committee" />
              <Column field="date" header="Date" />
              <Column field="time" header="Time" />
            </DataTable>
          </TabPanel>
        </TabView>
      </Panel>

      <!-- Quick Links Section -->
      <Panel header="Quick Links" class="mb-4">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <Card class="quick-link-card cursor-pointer">
            <template #title>LEDAC Bills</template>
            <template #content>
              <p>Legislative-Executive Development Advisory Council Bills</p>
            </template>
          </Card>
          <Card class="quick-link-card cursor-pointer" @click="openRedBills">
            <template #title>RED Bills</template>
            <template #content>
              <p>Bills passed by both chambers</p>
            </template>
          </Card>
          <Card class="quick-link-card cursor-pointer" @click="openAdvancingBills">
            <template #title>Advancing Bills</template>
            <template #content>
              <p>Bills currently in progress</p>
            </template>
          </Card>
        </div>
      </Panel>
    </div>

    <!-- Committee Report Dialog -->
    <Dialog v-model:visible="committeeReportVisible" header="Top 10 Active Committees" :style="{width: '50vw'}" :modal="true">
      <DataTable :value="committeeReportData" :loading="loadingReport" :paginator="true" :rows="10" v-model:filters="filters">
        <template #header>
          <div class="flex justify-content-end">
            <InputText v-model="filters['global'].value" placeholder="Search..." />
          </div>
        </template>
        <Column field="name" header="Committee Name" />
        <Column field="total_count" header="Total Meetings" />
      </DataTable>
    </Dialog>

    <!-- Request for Comments Dialog -->
    <Dialog v-model:visible="requestForCommentsVisible" header="Request for Comments" :style="{width: '80vw'}" :modal="true">
      <DataTable :value="requestForCommentsList" :loading="loadingRequestForCommentsList" :paginator="true" :rows="10" v-model:filters="filters">
        <template #header>
          <div class="flex justify-content-end">
            <InputText v-model="filters['global'].value" placeholder="Search..." />
          </div>
        </template>
        <Column field="Congress Period" header="Congress Period" />
        <Column field="House Bill No." header="House Bill No." />
        <Column field="Senate Bill No." header="Senate Bill No." />
        <Column field="Agenda" header="Agenda" />
        <Column field="Subject Title" header="Subject Title" />
      </DataTable>
    </Dialog>

    <!-- For Reviews Dialog -->
    <Dialog v-model:visible="forReviewsVisible" header="For Reviews" :style="{width: '80vw'}" :modal="true">
      <DataTable :value="forReviewsList" :loading="loadingForReviewsList" :paginator="true" :rows="10" v-model:filters="filters">
        <template #header>
          <div class="flex justify-content-end">
            <InputText v-model="filters['global'].value" placeholder="Search..." />
          </div>
        </template>
        <Column field="Congress Period" header="Congress Period" />
        <Column field="House Bill No." header="House Bill No." />
        <Column field="Senate Bill No." header="Senate Bill No." />
        <Column field="Agenda" header="Agenda" />
        <Column field="Subject Title" header="Subject Title" />
      </DataTable>
    </Dialog>

    <!-- Signed Position Papers Dialog -->
    <Dialog v-model:visible="signedPositionVisible" header="Signed Position Papers" :style="{width: '80vw'}" :modal="true">
      <DataTable :value="signedPositionList" :loading="loadingSignedPositionList" :paginator="true" :rows="10" v-model:filters="filters">
        <template #header>
          <div class="flex justify-content-end">
            <InputText v-model="filters['global'].value" placeholder="Search..." />
          </div>
        </template>
        <Column field="Congress Period" header="Congress Period" />
        <Column field="House Bill No." header="House Bill No." />
        <Column field="Senate Bill No." header="Senate Bill No." />
        <Column field="Agenda" header="Agenda" />
        <Column field="Subject Title" header="Subject Title" />
        <Column field="Signed position paper Link" header="Signed Position Paper Link">
          <template #body="{ data }">
            <a v-if="hasValidUrl(data['Signed position paper Link'])" :href="getUrlFromLink(data['Signed position paper Link'])" target="_blank" class="source-link">View Document</a>
            <span v-else>N/A</span>
          </template>
        </Column>
      </DataTable>
    </Dialog>

    <!-- Signed Into Law Dialog -->
    <Dialog v-model:visible="signedIntoLawVisible" header="Signed Into Law Bills" :style="{width: '70vw'}" :modal="true">
      <DataTable :value="signedIntoLawList" :loading="loadingSignedIntoLaw" :paginator="true" :rows="10" v-model:filters="filters">
        <template #header>
          <div class="flex justify-content-end">
            <InputText v-model="filters['global'].value" placeholder="Search..." />
          </div>
        </template>
        <Column field="title" header="Bill Title" />
        <Column field="nos" header="Republic Act No" />
        <Column field="status_date" header="Signed Date">
          <template #body="{ data }">
            {{ formatMeetingDate(data.status_date) }}
          </template>
        </Column>
        <Column field="source" header="Source Link">
          <template #body="{ data }">
            <a v-if="data.source" :href="data.source" target="_blank" class="source-link">View Source</a>
            <span v-else>N/A</span>
          </template>
        </Column>
      </DataTable>
    </Dialog>
  </div>
</template>

<script>
import Panel from 'primevue/panel'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Carousel from 'primevue/carousel'
import Tag from 'primevue/tag'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import Dialog from 'primevue/dialog'
import ProgressSpinner from 'primevue/progressspinner'
import api from '../axios.js'

export default {
  components: {
    Panel,
    Card,
    Button,
    Carousel,
    Tag,
    TabView,
    TabPanel,
    DataTable,
    Column,
    InputText,
    Dialog,
    ProgressSpinner
  },
  data() {
    return {
      searchTerm: '',
      currentNews: 0,
      activeTab: 'senate',
      committeeReportVisible: false,
      committeeReportData: [],
      loadingReport: false,
      signedIntoLawVisible: false,
      signedIntoLawCount: 0,
      requestForComments: 0,
      signedPositionCount: 0,
      loadingRequestForComments: false,
      loadingSignedPosition: false,
      loadingSignedIntoLaw: false,
      loadingForReview: false,
      forReviewCount: 0,
      requestForCommentsVisible: false,
      requestForCommentsList: [],
      loadingRequestForCommentsList: false,
      signedPositionVisible: false,
      signedPositionList: [],
      loadingSignedPositionList: false,
      forReviewsVisible: false,
      forReviewsList: [],
      loadingForReviewsList: false,
      signedIntoLawList: [],
      loadingSignedIntoLaw: false,
      filters: {
        global: { value: null }
      },
      countdown: {
        days: 0,
        hours: 0,
        minutes: 0
      },
      currentSession: {
        name: 'Loading...',
        dateFrom: null,
        dateTo: null
      },
      nextSession: {
        name: '',
        date: new Date()
      },
      newsItems: [],
      allMeetings: [],
      senateMeetings: [],
      houseMeetings: [],
      dofMeetings: [],
      top5UrgentBills: [
        {
          id: 1,
          subject_title: "OFW Remittance Protection Act",
          urgency: "High",
          bill_type: "LEDAC",
          status: "SBs pending at the Committee-level in the Senate",
          total_revenue_impact: "(PHP 9.38B to 11.73B)",
          total_budgetary_impact: "N/A",
          total_fiscal_impact: "(PHP 9.38B to 11.73B)",
          other_dof_concerns: "N/A",
          background_rationale: "Aims to protect OFW remittances against usurious interest and exorbitant fees charged by banks and non-banks financial intermediaries.",
          current_existing_regime: "Sec. 23(C) of the NIRC, as amended, treats an OFW as a non-resident citizen who is taxable only on income derived from sources within PH, hence, exempted from 20% FWT on interest income. Sec. 22 RA 10022 exempts remittances of OFWs from the disposition of DST at a rate of P0.60 for every P200 collected on all transfers and remittances from abroad and payable in the PH",
          proposal_salient_provisions: "Grant tax deduction to bank and non-bank financial intermediaries granting 50% discount on OFWs' remittance fees which shall be treated as an ordinary and necessary expense deductible from the gross income",
          fpmg_recommended_position: "Do not support. The SBs would further erode govt revenues and contradict the administration's efforts to rationalize and remove undue tax advantages in different sectors.",
          osec_instruction_remarks: ""
        },
        {
          id: 2,
          subject_title: "Amendments to/Repeal of Bank Secrecy and Foreign Currency Deposits Laws",
          urgency: "High",
          bill_type: "LEDAC",
          status: "Bills pending at the Senate committee-level",
          total_revenue_impact: "With potential positive impact but cannot be estimated (higher tax collection from previously undeclared assets, reduction in tax gap, and enforcement of penalties; possible short-term risk of capital flight but can be offset by long-term investment climate)",
          total_budgetary_impact: "N/A",
          total_fiscal_impact: "",
          other_dof_concerns: "",
          background_rationale: "Under Bank Secrecy Law (RA 1405), all deposits cannot be examined, inquired, or looked into except: (a) upon written permission of depositor, (b) in cases of impeachment, or (c) upon order of competent court (in cases of bribery or dereliction of duty, or in cases where the money deposited or invested is the subject matter of litigation). Under Foreign Currency Deposit (FCD) Act (RA 6426), as amended, all FCDs are confidential or covered by secrecy, except upon written permission of the depositor. Bank deposits and FCDs of government officials and employees are covered by RA 1405 and RA 6426",
          current_existing_regime: "Under Bank Secrecy Law (RA 1405), all deposits cannot be examined, inquired, or looked into except: (a) upon written permission of depositor, (b) in cases of impeachment, or (c) upon order of competent court",
          proposal_salient_provisions: "Either amend or repeal RA 1405 and provisions of RA 6426 and other related laws/provisions, or impose new requirements, to exclude government officials and employees from coverage of bank secrecy and confidentiality of FCDs. Mandate all government officials and employees (except those serving in honorary capacity) to execute a written waiver in favor of the Ombudsman to look into all their deposits including FCDs, as a condition to enter upon or continue in office. Prospectively exclude from bank secrecy BSP inquiry or examination in the exercise of its supervisory powers upon determination by the MB that there is reasonable ground for fraud, except during an election period.",
          fpmg_recommended_position: "Support amendment rather than repeal of the laws. Recommend to amend 'safeguards' to ensure effectiveness of the proposed lifting of bank secrecy. Recommend to include power of BIR Commissioner to inquire into bank deposits (RBDU and FCDU) for tax purposes.",
          osec_instruction_remarks: ""
        },
        {
          id: 3,
          subject_title: "Corporate Social Responsibility (CSR) Act",
          urgency: "High",
          bill_type: "",
          status: "Bills pending at the Committee-level in the House of Representatives",
          total_revenue_impact: "N/A",
          total_budgetary_impact: "N/A",
          total_fiscal_impact: "",
          other_dof_concerns: "",
          background_rationale: "It seeks to institutionalize CSR to encourage businesses to pursue sustainable socioeconomic development.",
          current_existing_regime: "RA 11232 or the Revised Corporation Code on retained earnings. SEC Memorandum Circular (MC) No. 16 Series of 2023, and SEC MC NO. 4, Series of 2019 on CSR Reporting. Sections 34(A)(1) and Section 34(H) of the Tax Code, on proposed tax exemptions",
          proposal_salient_provisions: "Deductibility of CSR expenses from the gross income. Exemption of CSR projects and programs from the prohibition against corporations to retain surplus profits in excess of their paid-in capital stock. Submission of CSR plans and reports for monitoring by the Securities and Exchange Commission (SEC). Extension of Local Government Unit (LGU) assistance.",
          fpmg_recommended_position: "We recommend removing the provision to allow CSR as deduction from unrestricted retained earnings as the CREATE Act already repealed Improperly Accumulated Earnings Tax (IAET). In addition, we maintain that any proposed tax exemptions be consistent with the Tax Code.",
          osec_instruction_remarks: ""
        }
      ],
    }
  },
  computed: {
    filteredSenateMeetings() {
      return this.senateMeetings.filter(meeting => 
        meeting.committee.toLowerCase().includes(this.searchTerm.toLowerCase())
      )
    },
    filteredHouseMeetings() {
      return this.houseMeetings.filter(meeting => 
        meeting.committee.toLowerCase().includes(this.searchTerm.toLowerCase())
      )
    },
    filteredDofMeetings() {
      return this.dofMeetings.filter(meeting => 
        meeting.committee.toLowerCase().includes(this.searchTerm.toLowerCase())
      )
    }
  },
  methods: {
    async loadCommitteeMeetings() {
      try {
        const response = await api.get('/committee-meetings')
        this.allMeetings = response.data
        
        // Filter meetings by chamber
        this.senateMeetings = this.allMeetings
          .filter(meeting => meeting.chamber === 'Senate')
          .map(meeting => ({
            id: meeting.id,
            committee: meeting.committee,
            date: this.formatMeetingDate(meeting.meeting_date),
            time: this.formatMeetingTime(meeting.meeting_time)
          }))
          
        this.houseMeetings = this.allMeetings
          .filter(meeting => meeting.chamber === 'House')
          .map(meeting => ({
            id: meeting.id,
            committee: meeting.committee,
            date: this.formatMeetingDate(meeting.meeting_date),
            time: this.formatMeetingTime(meeting.meeting_time)
          }))
          
        this.dofMeetings = this.allMeetings
          .filter(meeting => meeting.chamber === 'Other')
          .map(meeting => ({
            id: meeting.id,
            committee: meeting.committee,
            date: this.formatMeetingDate(meeting.meeting_date),
            time: this.formatMeetingTime(meeting.meeting_time)
          }))
      } catch (error) {
        console.error('Error loading committee meetings:', error)
        this.senateMeetings = []
        this.houseMeetings = []
        this.dofMeetings = []
      }
    },
    async loadNews() {
      try {
        const response = await api.get('/news')
        const allNews = response.data
        // Get latest 5 news items for carousel
        this.newsItems = allNews.slice(0, 5).map(news => ({
          title: news.headline,
          excerpt: news.summary.length > 150 ? news.summary.substring(0, 150) + '...' : news.summary,
          link: news.news_link,
          date: news.date_published
        }))
      } catch (error) {
        console.error('Error loading news:', error)
        this.newsItems = []
      }
    },
    openNewsLink(link) {
      window.open(link, '_blank')
    },
    nextNews() {
      this.currentNews = (this.currentNews + 1) % this.newsItems.length
    },
    prevNews() {
      this.currentNews = this.currentNews === 0 ? this.newsItems.length - 1 : this.currentNews - 1
    },
    async loadCurrentSession() {
      try {
        const response = await api.get('/current-session')
        const data = response.data
        
        // Set current session
        if (data.current) {
          this.currentSession = {
            name: `${data.current.congress_no}th Congress - ${data.current.session} Session${data.current.schedule ? ': ' + data.current.schedule : ''}`,
            dateFrom: data.current.date_from,
            dateTo: data.current.date_to
          }
        }
        
        // Set next session for countdown
        if (data.next) {
          this.nextSession = {
            name: `${data.next.congress_no}th Congress - ${data.next.session} Session: ${data.next.schedule}`,
            date: new Date(data.next.date_from)
          }
        } else {
          this.nextSession = { name: '', date: new Date() }
        }
      } catch (error) {
        console.error('Error loading current session:', error)
        this.currentSession = {
          name: '20th Congress - First Session',
          dateFrom: null,
          dateTo: null
        }
        this.nextSession = { name: '', date: new Date() }
      }
    },
    updateCountdown() {
      const now = new Date().getTime()
      const sessionTime = this.nextSession.date.getTime()
      const distance = sessionTime - now
      
      if (distance > 0) {
        this.countdown.days = Math.floor(distance / (1000 * 60 * 60 * 24))
        this.countdown.hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        this.countdown.minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      } else {
        this.countdown = { days: 0, hours: 0, minutes: 0 }
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'TBD'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatMeetingDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatMeetingTime(timeString) {
      if (!timeString) return 'N/A'
      const timeParts = timeString.split(':')
      const hours = parseInt(timeParts[0])
      const minutes = timeParts[1]
      const ampm = hours >= 12 ? 'PM' : 'AM'
      const displayHours = hours % 12 || 12
      return `${displayHours}:${minutes} ${ampm}`
    },
    openSenateStream() {
      window.open('https://www.youtube.com/@senateofthephilippines', '_blank')
    },
    openHouseStream() {
      window.open('https://www.youtube.com/@HouseofRepresentativesPH', '_blank')
    },
    async showCommitteeReport() {
      this.committeeReportVisible = true
      this.loadingReport = true
      try {
        const response = await api.get('/committee-meetings-report')
        this.committeeReportData = response.data
      } catch (error) {
        console.error('Error loading committee report:', error)
        this.committeeReportData = []
      } finally {
        this.loadingReport = false
      }
    },
    async loadSignedPositionPaperCount() {
      this.loadingSignedPosition = true
      try {
        const response = await api.get('/getSignedPositionPapers')
        this.signedPositionCount = response.data.count
        this.signedIntoLawList = response.data.result
      } catch (error) {
        console.error('Error loading signed position paper count:', error)
        this.signedPositionCount = 0
      } finally {
        this.loadingSignedPosition = false
      }
    },
    async loadRequestForCommentsCount() {
      this.loadingRequestForComments = true
      try {
        const response = await api.get('/getPositionPaperRequests')
        this.requestForComments = response.data.count
        this.requestForCommentsList = response.data.result
      } catch (error) {
        console.error('Error loading request for comments count:', error)
        this.requestForComments = 0
      } finally {
        this.loadingRequestForComments = false
      }
    },
    async loadForReviewCount() {
      this.loadingForReview = true
      try {
        const response = await api.get('/getForReviews')
        this.forReviewCount = response.data.count
        this.forReviewsList = response.data.result

      } catch (error) {
        console.error('Error loading request for comments count:', error)
        this.forReviewCount = 0
      } finally {
        this.loadingForReview = false
      }
    },
    async showRequestForCommentsModal() {
      this.requestForCommentsVisible = true
      this.loadingRequestForCommentsList = true
      try {
        
        this.requestForCommentsList = this.requestForCommentsList
      } catch (error) {
        console.error('Error loading request for comments list:', error)
        this.requestForCommentsList = []
      } finally {
        this.loadingRequestForCommentsList = false
      }
    },
    async showSignedPositionModal() {
      this.signedPositionVisible = true
      this.loadingSignedPositionList = true
      try {
       
        this.signedPositionList = this.signedIntoLawList
      } catch (error) {
        console.error('Error loading signed position papers list:', error)
        this.signedPositionList = []
      } finally {
        this.loadingSignedPositionList = false
      }
    },
    async showForReviewsModal() {
      this.forReviewsVisible = true
      this.loadingForReviewsList = true
      try {
       
        this.forReviewsList = this.forReviewsList
      } catch (error) {
        console.error('Error loading for reviews list:', error)
        this.forReviewsList = []
      } finally {
        this.loadingForReviewsList = false
      }
    },
    hasValidUrl(linkString) {
      if (!linkString) return false
      try {
        const linkObj = JSON.parse(linkString)
        return linkObj.Url && linkObj.Url.trim() !== ''
      } catch {
        return false
      }
    },
    getUrlFromLink(linkString) {
      try {
        const linkObj = JSON.parse(linkString)
        return linkObj.Url
      } catch {
        return ''
      }
    },
    openRedBills() {
      window.open('/red-bills', '_blank')
    },
    async openAdvancingBills() {
      try {
        const response = await api.get('/advancing-bills/print', { responseType: 'blob' })
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
      } catch (error) {
        console.error('Error opening advancing bills PDF:', error)
      }
    },
    getUrgencySeverity(urgency) {
      switch(urgency) {
        case 'High': return 'danger'
        case 'Medium': return 'warn'
        case 'Low': return 'info'
        default: return 'secondary'
      }
    },
    viewSubject(id){
      router.push(`/subject/view/${id}`)
    },
    async printTop5Urgent() {
      try {
        const response = await api.get('/bills-by-subject/top5/urgent/print', { responseType: 'blob' })
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
      } catch (error) {
        console.error('Error printing top 5 urgent bills:', error)
      }
    }
  },
  async mounted() {
    this.loadNews()
    this.loadCommitteeMeetings()
    await this.loadCurrentSession()
    this.loadSignedPositionPaperCount()
    this.loadRequestForCommentsCount()
    this.loadForReviewCount()

    this.updateCountdown()
    
    // Auto-slide news every 5 seconds
    setInterval(() => {
      if (this.newsItems.length > 0) {
        this.nextNews()
      }
    }, 5000)
    
    // Update countdown every minute
    setInterval(this.updateCountdown, 60000)
  }
}
</script>

<style scoped>
.datetime-section {
  text-align: center;
  margin: 2rem 0;
  padding: 1.5rem;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
}

.current-date {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.current-time {
  font-size: 2rem;
  font-weight: 700;
  color: #fbbf24;
}

.page {
  padding: 2rem;
  background: var(--p-surface-50);
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.page h1 {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  text-align: center;
  color: var(--p-text-color);
}

.page p {
  font-size: 1.1rem;
  text-align: center;
  margin-bottom: 2rem;
  color: var(--p-text-muted-color);
}

:deep(.p-panel-header) {
  font-size: 1.3rem !important;
  font-weight: bold !important;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%) !important;
  color: white !important;
  border-left: 4px solid #fbbf24 !important;
}

:deep(.p-card) {
  border: none !important;
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%) !important;
  box-shadow: 0 8px 32px rgba(30, 58, 138, 0.12) !important;
  border-radius: 16px !important;
  overflow: hidden !important;
  position: relative !important;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

:deep(.p-card::before) {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  height: 4px !important;
  background: linear-gradient(90deg, #1e3a8a 0%, #3b82f6 50%, #fbbf24 100%) !important;
}

:deep(.p-card:hover) {
  transform: translateY(-8px) scale(1.02) !important;
  box-shadow: 0 20px 40px rgba(30, 58, 138, 0.2) !important;
  background: linear-gradient(145deg, #ffffff 0%, #f1f5f9 100%) !important;
}

:deep(.p-card-body) {
  padding: 2rem !important;
}

:deep(.p-card-title) {
  color: #1e3a8a !important;
  font-weight: 700 !important;
  font-size: 1.1rem !important;
  margin-bottom: 1rem !important;
  text-align: center !important;
}

:deep(.p-card-content) {
  text-align: center !important;
  color: #64748b !important;
}

.news-item {
  text-align: center;
  padding: 2rem;
}

.news-item h3 {
  font-size: 1.1rem;
  margin-bottom: 1rem;
  line-height: 1.3;
}

.news-item p {
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 0.5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.news-date {
  color: #6b7280;
  font-style: italic;
  display: block;
  margin-top: 0.5rem;
}

.news-headline-link {
  color: #1e3a8a;
  text-decoration: none;
  transition: color 0.3s;
}

.news-headline-link:hover {
  color: #3b82f6;
  text-decoration: underline;
}

.mt-3 {
  margin-top: 0.75rem;
}

.stream-card {
  position: relative;
  overflow: hidden;
}

.stream-content {
  text-align: center;
  padding: 1rem 0;
}

.stream-info {
  margin-bottom: 1.5rem;
}

.stream-info p {
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
}

.stream-btn {
  width: 100%;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.stream-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(220, 38, 38, 0.3);
}

.countdown-container {
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem 1rem 1.5rem 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(30, 58, 138, 0.08);
}

.countdown-container h3 {
  color: #1e3a8a;
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  text-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.session-dates {
  margin: 1rem 0;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(30, 58, 138, 0.1);
}

.date-range {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.date-item {
  color: #1e3a8a;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(30, 58, 138, 0.1);
}

.date-separator {
  color: #fbbf24;
  font-size: 1.5rem;
  font-weight: bold;
}

.next-session-container {
  text-align: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fbbf24 100%);
  border-radius: 12px;
  border: 1px solid #f59e0b;
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.2);
}

.next-session-container h4 {
  color: #92400e;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.countdown-display {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.tile-count {
  font-size: 2.5rem;
  font-weight: bold;
  color: #002688;
  margin: 1rem 0;
  text-align: center;
}

.quick-link-card:hover {
  transform: translateY(-2px);
  transition: transform 0.3s;
}

:deep(.p-button) {
  background: #1e3a8a !important;
  border-color: #1e3a8a !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
}

:deep(.p-button:hover) {
  background: #1e40af !important;
  border-color: #1e40af !important;
  transform: translateY(-1px);
}

:deep(.p-button-outlined) {
  color: white !important;
  background: #1e3a8a !important;
  border-color: #1e3a8a !important;
}

:deep(.p-button-outlined:hover) {
  background: #1e40af !important;
  color: white !important;
}

:deep(.p-tag) {
  font-weight: 700 !important;
  border-radius: 25px !important;
  padding: 0.75rem 1.5rem !important;
  font-size: 1.1rem !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
  transition: all 0.3s ease !important;
}

:deep(.p-tag:hover) {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(0,0,0,0.2) !important;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.mt-6 {
  margin-top: 2rem;
}

.flex {
  display: flex;
}

.flex-1 {
  flex: 1;
}

.flex-wrap {
  flex-wrap: wrap;
}

.gap-4 {
  gap: 1rem;
}

.grid {
  display: grid;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

.committee-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
  gap: 1rem;
}

.search-input {
  flex: 1;
  max-width: 300px;
}

.print-btn {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
}

:deep(.p-card) {
  cursor: pointer;
}

.source-link {
  color: #1e3a8a;
  text-decoration: none;
  font-weight: 500;
}

.source-link:hover {
  text-decoration: underline;
  color: #3b82f6;
}

.tile-loading {
  background: #f0f0f0 !important;
  opacity: 0.7;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem 0;
  font-size: 1.1rem;
  color: #666;
  font-weight: 500;
}

@media (min-width: 768px) {
  .md\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  .md\:grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .lg\:grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .page {
    padding: 1rem;
  }
  
  .page h1 {
    font-size: 2rem;
  }
  
  .current-date {
    font-size: 1.2rem;
  }
  
  .current-time {
    font-size: 1.5rem;
  }
}
</style>